'use client';

import React, {
  CSSProperties,
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useSurfsideContext,
  useSurfsideProductsResponse,
} from '@surfside/ads-react';
import type { ColorField, ImageField } from '@prismicio/client';
import classNames from 'classnames';

import {
  Heading,
  IconSvg,
  Product,
  ProductPlaceholder,
  Slider,
  useData,
} from '@/components';
import { useProductsByIds } from '@/hooks';
import { GProduct } from '@/types';
import { getSlicePadding } from '@/utils';

import {
  EnrichedProduct,
  getSurfsideProductId,
  getSurfsideProductType,
  IMAGE_PIXEL_METHOD,
  VIEWABILITY_DWELL_MS,
  VIEWABILITY_THRESHOLD,
  VIEWABLE_MRC50_EVENT,
} from './utils';
import { useCarouselTracking } from './useCarouselTracking';

import adsStyles from './ads.module.scss';
import styles from './adsCarousel.module.scss';

interface AdsCarouselProps {
  zoneId: string;
  locationId: string;
  strategy: 'sponsored' | 'hybrid' | 'recommended';
  recommenderType?: 'for-you' | 'top-products' | 'past-purchases';
  pageSize?: number;
  maxProducts?: number;
  category?: string | null;
  title?: string;
  titleColor?: ColorField;
  activeControlColor?: ColorField;
  disableControlColor?: ColorField;
  loadMoreText?: string | null;
  loadMoreTextColor?: ColorField;
  analyticsListName?: string;
  backgroundColor?: ColorField;
  backgroundImage?: ImageField<never>;
  backgroundStyleFixed?: boolean;
  minHeight?: number | null;
  paddingSection?: 'small' | 'medium' | 'large' | null;
  spacingBottom?: boolean;
}

const AdsCarousel: FC<AdsCarouselProps> = ({
  zoneId,
  locationId,
  strategy,
  recommenderType = 'top-products',
  pageSize = 6,
  maxProducts = 20,
  category,
  title,
  titleColor,
  activeControlColor,
  disableControlColor,
  loadMoreText,
  loadMoreTextColor,
  analyticsListName = 'ads-carousel',
  backgroundColor,
  backgroundImage,
  backgroundStyleFixed = false,
  minHeight,
  paddingSection = 'large',
  spacingBottom = false,
}) => {
  const { productCard } = useData();
  const {
    breakpoints,
    defaultSlidePerView,
    defaultSpaceBetween,
    spacingBetweenCard,
    isLargeCard,
  } = productCard.displayProps;

  const [isPaginating, setIsPaginating] = useState(false);

  const categories = useMemo(() => {
    const parsed = category
      ?.split(',')
      .map(item => item.trim())
      .filter(Boolean);

    return parsed?.length ? parsed : undefined;
  }, [category]);

  const enabled = !!zoneId && !!locationId;
  const {
    isReady,
    defaultNative,
    startRequest,
    buildSponsoredCarousel,
    buildHybridCarousel,
    buildRecommendedCarousel,
  } = useSurfsideContext();

  const carousel = useMemo(() => {
    if (
      !isReady ||
      !enabled ||
      !defaultNative ||
      !startRequest ||
      !buildSponsoredCarousel ||
      !buildHybridCarousel ||
      !buildRecommendedCarousel
    ) {
      return undefined;
    }

    if (strategy === 'recommended') {
      return buildRecommendedCarousel({ type: recommenderType });
    }

    const request = startRequest(zoneId)
      .withNative(
        defaultNative()
          .withMetaProduct(80, false)
          .withMetaBrand(80, false)
          .withEventTracker(VIEWABLE_MRC50_EVENT, [IMAGE_PIXEL_METHOD])
          .build(),
      )
      .withSurfCategory(categories ?? [])
      .build();

    if (strategy === 'hybrid') {
      return buildHybridCarousel({ type: recommenderType }, request);
    }

    return buildSponsoredCarousel(request);
  }, [
    buildHybridCarousel,
    buildRecommendedCarousel,
    buildSponsoredCarousel,
    categories,
    defaultNative,
    enabled,
    isReady,
    recommenderType,
    startRequest,
    strategy,
    zoneId,
  ]);

  const responseRecommenderType =
    strategy === 'sponsored' ? undefined : recommenderType;
  const surfsideProductsOptions = useMemo(
    () => ({
      carousel,
      category: categories,
      enabled,
      maxProducts,
      pageSize,
      recommenderType: responseRecommenderType,
      strategy,
      withProductInfo: false as const,
      zoneId,
    }),
    [
      carousel,
      categories,
      enabled,
      maxProducts,
      pageSize,
      responseRecommenderType,
      strategy,
      zoneId,
    ],
  );
  const {
    products: surfsideProducts,
    loading: surfsideLoading,
    error: surfsideError,
    hasMore,
    fetchNextPage,
    analytics,
  } = useSurfsideProductsResponse(surfsideProductsOptions);

  const productIds = useMemo(
    () =>
      (surfsideProducts ?? [])
        .map(getSurfsideProductId)
        .filter((id): id is string => Boolean(id)),
    [surfsideProducts],
  );

  const typeById = useMemo(() => {
    const map = new Map<string, 'sponsored' | 'recommended'>();
    surfsideProducts?.forEach(product => {
      const id = getSurfsideProductId(product);
      const type = getSurfsideProductType(product);
      if (id && type) {
        map.set(id, type);
      }
    });
    return map;
  }, [surfsideProducts]);

  const {
    products: hydratedProducts,
    isLoading: isHydrating,
    error: hydrationError,
  } = useProductsByIds({ ids: productIds });

  const enrichedProducts = useMemo(() => {
    const byId = new Map(
      hydratedProducts
        .filter((product): product is GProduct & { id: string } =>
          Boolean(product.id),
        )
        .map(product => [String(product.id).trim(), product]),
    );

    const products: EnrichedProduct[] = [];

    productIds.forEach(id => {
      const product = byId.get(id);
      if (!product) return;

      products.push({
        ...product,
        surfsideType: typeById.get(id),
      });
    });

    return products;
  }, [hydratedProducts, productIds, typeById]);

  const { onProductClick, onProductViewable } = useCarouselTracking(
    surfsideProducts,
    enrichedProducts,
    analytics,
  );

  useEffect(() => {
    if (!isPaginating) return;
    if (surfsideLoading || isHydrating) return;

    const timeoutId = window.setTimeout(() => {
      setIsPaginating(false);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [isPaginating, surfsideLoading, isHydrating]);

  const handleLoadMore = () => {
    if (!hasMore || surfsideLoading || isPaginating || !fetchNextPage) return;
    setIsPaginating(true);
    void fetchNextPage();
  };

  const isInitialLoading =
    productIds.length > 0 &&
    enrichedProducts.length === 0 &&
    !hydrationError &&
    isHydrating;
  const isLoadingMore = isPaginating && (surfsideLoading || isHydrating);
  const hasError = !!surfsideError || !!hydrationError;
  const hasProducts = enrichedProducts.length > 0;

  const sectionAriaLabel = title
    ? `Recommended products: ${title}`
    : 'Recommended products';
  const carouselAriaLabel = title
    ? `${title} carousel`
    : 'Recommended products carousel';

  // Third-party / empty: hide entire slice (no error UI)
  if (!isInitialLoading && (hasError || !hasProducts)) {
    return null;
  }

  const carouselStyle = {
    ...(titleColor ? { '--ads-title-color': titleColor } : {}),
    ...(activeControlColor
      ? { '--ads-control-color': activeControlColor }
      : {}),
    ...(disableControlColor
      ? { '--ads-control-disabled-color': disableControlColor }
      : {}),
    ...(loadMoreTextColor
      ? { '--ads-load-more-color': loadMoreTextColor }
      : {}),
  } as CSSProperties;

  const sectionStyle: CSSProperties = {
    background: backgroundColor ?? undefined,
    backgroundImage: backgroundImage?.url
      ? `url(${backgroundImage.url})`
      : undefined,
    minHeight: `${minHeight || 0}vh`,
    ...carouselStyle,
  };

  const loadMoreDisabled =
    !hasMore || isPaginating || isInitialLoading || !fetchNextPage;
  const loadMoreLabel = loadMoreText?.trim() || 'Load more';

  return (
    <section
      aria-busy={isInitialLoading || isLoadingMore}
      aria-label={sectionAriaLabel}
      className={classNames(
        adsStyles.section__ads,
        adsStyles.section__ads_carousel,
        getSlicePadding(paddingSection ?? 'large'),
        styles.ads_carousel,
        {
          ['spacing_bottom']: spacingBottom,
          ['background__fixed_cover']: backgroundStyleFixed,
          ['background__contained']: !backgroundStyleFixed,
        },
      )}
      style={sectionStyle}
    >
      <div aria-live="polite" className="sr-only">
        {isInitialLoading
          ? 'Loading sponsored products'
          : isLoadingMore
          ? 'Loading more products'
          : undefined}
      </div>
      <Slider
        activatedHover
        ariaLabel={carouselAriaLabel}
        breakpoints={breakpoints}
        centeredSlides={false}
        className={styles.products__carousel}
        freeMode
        isNotPaddingMobile={spacingBetweenCard === 'none'}
        key={isInitialLoading ? 'loading' : 'loaded'}
        seeAllElement={
          <button
            aria-busy={isLoadingMore}
            aria-disabled={loadMoreDisabled}
            aria-label={`${loadMoreLabel} products`}
            className={styles.show_more_header}
            disabled={loadMoreDisabled}
            onClick={handleLoadMore}
            type="button"
          >
            {loadMoreLabel}
          </button>
        }
        showSliderCardLink={true}
        sliderHeading={
          title ? (
            <Heading className={styles.title} level={2}>
              {title}
            </Heading>
          ) : undefined
        }
        slidesPerView={defaultSlidePerView}
        spaceBetween={defaultSpaceBetween}
      >
        {isInitialLoading
          ? Array.from({ length: pageSize }).map((_, index) => (
              <ProductPlaceholder key={index} largeCard={isLargeCard} />
            ))
          : enrichedProducts.map(product => (
              <ProductWrapper
                analyticsListName={analyticsListName}
                key={product.id}
                onProductClick={onProductClick}
                onProductViewable={onProductViewable}
                product={product}
                reserveSponsoredSpace={strategy === 'hybrid'}
              />
            ))}
        <div className={styles.slide__main}>
          <button
            aria-busy={isLoadingMore}
            aria-disabled={loadMoreDisabled}
            aria-label={`${loadMoreLabel} products`}
            className={styles.show_more_slide}
            disabled={loadMoreDisabled}
            onClick={handleLoadMore}
            type="button"
          >
            <span
              className={classNames(styles.load_more_icon, {
                [styles.load_more_icon_spinning]: isLoadingMore,
              })}
            >
              <IconSvg name="refresh" />
            </span>
            <p>{loadMoreLabel}</p>
          </button>
          {/* Same footprint as Sponsored badge so the CTA matches product card height */}
          <span aria-hidden className={styles.show_more_slide_spacer} />
        </div>
      </Slider>
    </section>
  );
};

const CLICK_MOVE_THRESHOLD_PX = 10;

interface ProductWrapperProps {
  product: EnrichedProduct;
  analyticsListName?: string;
  onProductClick: (_product: EnrichedProduct) => void;
  onProductViewable: (_product: EnrichedProduct) => void;
  reserveSponsoredSpace?: boolean;
}

const ProductWrapper: FC<ProductWrapperProps> = ({
  analyticsListName,
  onProductClick,
  onProductViewable,
  product: productData,
  reserveSponsoredSpace = false,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const pointerOrigin = useRef<{ x: number; y: number } | null>(null);
  const productRef = useRef(productData);
  productRef.current = productData;
  const productId = productData.id ? String(productData.id).trim() : undefined;
  const isSponsored = productData.surfsideType === 'sponsored';
  const sponsoredLabelId = productId
    ? `ads-sponsored-${productId}`
    : 'ads-sponsored-label';

  useEffect(() => {
    const card = cardRef.current;
    if (!card || !isSponsored) return;

    let timer: number | undefined;
    let viewableFired = false;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[entries.length - 1];
        if (!entry) return;

        const isViewable =
          entry.isIntersecting &&
          entry.intersectionRatio >= VIEWABILITY_THRESHOLD;

        if (isViewable && !viewableFired) {
          if (timer !== undefined) return;

          timer = window.setTimeout(() => {
            viewableFired = true;
            timer = undefined;
            onProductViewable(productRef.current);
          }, VIEWABILITY_DWELL_MS);
          return;
        }

        window.clearTimeout(timer);
        timer = undefined;
      },
      { threshold: [VIEWABILITY_THRESHOLD] },
    );

    observer.observe(card);

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [isSponsored, onProductViewable, productId]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    pointerOrigin.current = { x: e.clientX, y: e.clientY };
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const isProductLink = target.closest('a[href*="/product/"]');
    if (!isProductLink) return;

    const origin = pointerOrigin.current;
    pointerOrigin.current = null;
    if (e.detail > 0 && origin) {
      const movedX = Math.abs(e.clientX - origin.x);
      const movedY = Math.abs(e.clientY - origin.y);
      if (
        movedX > CLICK_MOVE_THRESHOLD_PX ||
        movedY > CLICK_MOVE_THRESHOLD_PX
      ) {
        return;
      }
    }

    onProductClick(productData);
  };

  return (
    // Tap vs swipe for the product link inside. Keyboard still fires click on the inner <a>.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      aria-describedby={isSponsored ? sponsoredLabelId : undefined}
      className={styles.slide__main}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      ref={cardRef}
    >
      <Product
        analyticsListName={analyticsListName}
        className={styles.product}
        product={productData}
      />
      {(isSponsored || reserveSponsoredSpace) && (
        <span
          aria-hidden={isSponsored ? undefined : true}
          className={classNames(styles.sponsored_badge, {
            [styles.sponsored_badge_reserved]: !isSponsored,
          })}
          id={isSponsored ? sponsoredLabelId : undefined}
        >
          Sponsored
        </span>
      )}
    </div>
  );
};

export default AdsCarousel;
